import { consume } from '@lit/context';
import { LitElement, css, html, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';

import { gameApiContext } from '../../../context';
import type { GameAPIType } from '../../../GameAPI';
import { tokens } from '../../../theme/tokens.css';
import type { BindName } from '../../../../game/types/Input';
import { uiState as uiStateProxy } from '../../../UIState';
import { bindValtio } from '../../../ValtioController';

/**
 * Menu Toolbar - Top-right toolbar with sound, fullscreen, and menu buttons
 *
 * Provides quick access to:
 * - Sound toggle (mute/unmute)
 * - Fullscreen toggle
 * - Main menu drawer
 *
 * The mobile-only loadout and scoreboard controls are chips on the left of
 * the top bar (see airmash-game), not here.
 *
 * @element menu-toolbar
 */
@customElement('menu-toolbar')
export class MenuToolbar extends LitElement {
  static override styles = [
    tokens,
    css`
      :host {
        position: fixed;
        /* + the status-bar inset, in step with the top bar (see airmash-game). */
        top: calc(12px + env(safe-area-inset-top, 0px));
        /* Clear of the landscape notch on the right edge; 0 off-notch. */
        right: calc(12px + env(safe-area-inset-right, 0px));
        display: flex;
        gap: 0;
        z-index: 150;
        background-color: var(--color-bg-chip);
        border-radius: var(--radius-md);
        pointer-events: auto;
        /* Declared here rather than inherited from the body — the icons are
           <img>s, so a long-press mustn't raise iOS's "save image" callout or
           start a selection. */
        -webkit-user-select: none;
        user-select: none;
        -webkit-touch-callout: none;
      }

      .toolbar-btn {
        position: relative;
        width: 33px;
        height: 31px;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: transparent;
        border: none;
        border-radius: 0;
        cursor: pointer;
        transition: background-color 0.2s;
        padding: 0;
        outline: none;
        /* Kill iOS's ~300ms double-tap-zoom hesitation. Without this, a
           tap that swaps the icon mid-gesture (e.g. menu open → close
           flips the icon from "menu" to "X") can dispatch a delayed
           synthetic click that lands on the now-different visual target,
           toggling the menu a second time. */
        touch-action: manipulation;
        -webkit-tap-highlight-color: transparent;
      }

      .toolbar-btn:first-child {
        border-top-left-radius: var(--radius-md);
        border-bottom-left-radius: var(--radius-md);
      }

      .toolbar-btn:last-child {
        border-top-right-radius: var(--radius-md);
        border-bottom-right-radius: var(--radius-md);
      }

      .toolbar-btn + .toolbar-btn {
        border-left: 1px solid var(--color-border-subtle);
      }

      .toolbar-btn img {
        width: 20px;
        height: 20px;
        filter: brightness(0) invert(1);
        opacity: 0.8;
        /* Block mouse-drag ghost image of the icon on desktop. */
        -webkit-user-drag: none;
      }

      /* Touch: the system cluster drops the joined-strip look and becomes
         discrete 44×44 chips with gaps, matching the chips on the left of the
         top bar so the two toolbars read as one family. Each button gets its
         own rounded background; the host's shared background + dividers fall
         away. Desktop keeps the strip (hover: hover). */
      @media (hover: none) {
        :host {
          /* The top bar's 14px. Desktop's 12px centres the 31px strip on the
             top bar's 28px row; at 44px both rows are the same height, and
             12px sat this one 2px high. */
          top: calc(14px + env(safe-area-inset-top, 0px));
          background-color: transparent;
          border-radius: 0;
          gap: 6px;
        }
        .toolbar-btn {
          width: 44px;
          height: 44px;
          background-color: var(--color-bg-chip);
          border-radius: var(--radius-md);
        }
        .toolbar-btn + .toolbar-btn {
          border-left: none;
        }
        .toolbar-btn:active {
          background-color: var(--color-bg-selector-hover);
        }
        /* The chips' icon size and strength (theme/toolbar.css.ts). */
        .toolbar-btn img {
          width: 22px;
          height: 22px;
          opacity: 0.85;
        }
      }

      /* Hover-only — gated so iOS sticky-hover doesn't trap the chip in
         the "hovered" look (lit background + tooltip visible) after a tap. */
      @media (hover: hover) {
        .toolbar-btn:hover {
          background-color: var(--color-bg-selector-hover);
        }

        .toolbar-btn:hover img {
          opacity: 1;
        }
      }

      /* Tooltip styles */
      .tooltip {
        display: none;
        position: absolute;
        top: 100%;
        left: 50%;
        transform: translateX(-50%);
        margin-top: 8px;
        padding: 8px 10px;
        background-color: var(--color-bg-tooltip);
        border-radius: var(--radius-sm);
        font-family: var(--font-family);
        font-size: var(--font-size-sm);
        font-weight: normal;
        line-height: 1.4;
        color: var(--color-text-tooltip);
        white-space: nowrap;
        text-align: center;
        z-index: 100;
        pointer-events: none;
      }

      .tooltip .arrow {
        position: absolute;
        top: -6px;
        left: 50%;
        transform: translateX(-50%);
        width: 0;
        height: 0;
        border-left: 6px solid transparent;
        border-right: 6px solid transparent;
        border-bottom: 6px solid var(--color-bg-tooltip);
      }

      @media (hover: hover) {
        .toolbar-btn:hover .tooltip {
          display: block;
        }
      }

      /* Right-aligned tooltip for buttons near the edge */
      .tooltip.right-align {
        left: auto;
        right: 0;
        transform: none;
      }

      .tooltip.right-align .arrow {
        left: auto;
        right: 10px;
        transform: none;
      }
    `,
  ];

  /** UI state from context */
  private uiState = bindValtio(this, uiStateProxy);

  /** Game API from context */
  @consume({ context: gameApiContext })
  private gameApi!: GameAPIType;

  /** Toggle first, then click. Turning sound off stops every playing sound,
   *  and nothing plays until it's back on, so clicking first was never heard.
   *  This way, turning sound on clicks and turning it off is silent. */
  private handleSoundClick(): void {
    this.gameApi.toggleSound();
    this.gameApi.playClick();
  }

  private handleFullscreenClick(): void {
    // Fullscreen entry is user-activation-gated. Fire the privileged browser
    // call first so no side effect can interpose between the gesture and it.
    this.gameApi.toggleFullscreen();
    this.gameApi.playClick();
  }

  private handleMenuClick(e: Event): void {
    e.stopPropagation();
    this.gameApi.playClick();
    this.gameApi.toggleMainMenu();
  }

  /** Format a shortcut hint like " (G)" for a bind, tracking user remaps.
   *  Returns an empty string when the action is unbound so the tooltip
   *  simply omits the hint. */
  private keyHint(bindName: BindName): string {
    const key = this.uiState.keybinds.bindByName[bindName];
    return key ? ` (${key})` : '';
  }

  override render() {
    const { isSoundEnabled, isFullscreen, canFullscreen, activePanel } = this.uiState;
    const isMenuOpen = activePanel === 'mainmenu';

    const soundIcon = isSoundEnabled ? '/vanilla/ui/menu/sound-on.svg' : '/vanilla/ui/menu/sound-off.svg';

    const fullscreenIcon = isFullscreen ? '/vanilla/ui/menu/minimize.svg' : '/vanilla/ui/menu/maximize.svg';

    const menuIcon = isMenuOpen ? '/vanilla/ui/menu/x.svg' : '/vanilla/ui/menu/menu.svg';

    return html`
      ${
        isMenuOpen
          ? nothing
          : html`
              <button class="toolbar-btn" @click=${this.handleSoundClick}>
                <img src=${soundIcon} alt=${isSoundEnabled ? 'Sound on' : 'Sound off'} />
                <span class="tooltip">
                  <span class="arrow"></span>
                  ${isSoundEnabled ? 'Mute' : 'Unmute'}${this.keyHint('SOUND')}
                </span>
              </button>

              ${
                canFullscreen
                  ? html`
                      <button class="toolbar-btn" @click=${this.handleFullscreenClick}>
                        <img src=${fullscreenIcon} alt=${isFullscreen ? 'Exit fullscreen' : 'Fullscreen'} />
                        <span class="tooltip">
                          <span class="arrow"></span>
                          ${isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}${this.keyHint('FULLSCREEN')}
                        </span>
                      </button>
                    `
                  : nothing
              }
            `
      }

      <button class="toolbar-btn" @click=${this.handleMenuClick}>
        <img src=${menuIcon} alt=${isMenuOpen ? 'Close menu' : 'Menu'} />
        ${
          isMenuOpen
            ? nothing
            : html`
                <span class="tooltip right-align">
                  <span class="arrow"></span>
                  Menu${this.keyHint('MAINMENU')}
                </span>
              `
        }
      </button>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'menu-toolbar': MenuToolbar;
  }
}
